/**
 * PAY-4631: easing, geometry and drawing primitives shared by the
 * construction celebrations (`construction-scene.ts`, and the second ten in
 * `construction-scene-second-ten.ts`, PAY-4644), plus the builder contract
 * they all implement.
 *
 * These scenes are evaluated from time alone:
 * every prop and piece has a closed-form position at `t`, so a frame can be
 * drawn (or a test can sample one) at any moment without stepping.
 */

import type { Rng } from './celebration-variety';

export const TAU = Math.PI * 2;

export const clamp = (value: number, min = 0, max = 1) =>
  Math.max(min, Math.min(max, value));
export const lerp = (from: number, to: number, p: number) =>
  from + (to - from) * p;
/** 0 before `start`, 1 after `end`, linear between. */
export const seg = (t: number, start: number, end: number) =>
  clamp((t - start) / (end - start));

export const easeOut = (p: number) => 1 - Math.pow(1 - p, 3);
export const easeIn = (p: number) => p * p * p;
export const easeInOut = (p: number) =>
  p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
export const easeBack = (p: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2);
};
/** First contact is at p = 1 / 2.75. */
export const easeBounce = (p: number) => {
  const n = 7.5625;
  const d = 2.75;
  if (p < 1 / d) return n * p * p;
  if (p < 2 / d) return n * (p - 1.5 / d) * (p - 1.5 / d) + 0.75;
  if (p < 2.5 / d) return n * (p - 2.25 / d) * (p - 2.25 / d) + 0.9375;
  return n * (p - 2.625 / d) * (p - 2.625 / d) + 0.984375;
};
export const BOUNCE_CONTACT = 1 / 2.75;

export const rand = (rng: Rng, min: number, max: number) =>
  min + rng() * (max - min);
export function pick<T>(rng: Rng, items: readonly T[]): T {
  return items[Math.min(items.length - 1, Math.floor(rng() * items.length))]!;
}

export interface Point {
  x: number;
  y: number;
}

/**
 * The viewport and the notice's resting rectangle, in CSS pixels. Props are
 * laid out against the card (slings on its corners, a bucket under it), so
 * the scene works wherever the card actually rests.
 */
export interface StageGeometry {
  width: number;
  height: number;
  /** Card resting rectangle. */
  left: number;
  top: number;
  right: number;
  bottom: number;
  cardWidth: number;
  cardHeight: number;
  /** Card resting centre. */
  cx: number;
  cy: number;
  dark: boolean;
}

/** A style's card position in stage coordinates; anything omitted is at rest. */
export interface CardPlacement {
  x?: number;
  y?: number;
  rotation?: number;
  scale?: number;
  opacity?: number;
}

/** One built style: a `draw` for the canvas and a `place` for the card. */
export interface ConstructionScene {
  /**
   * From here on the card is at rest for good. Before it, a pose can pass
   * through rest mid-bounce or mid-shake, so the card is only released (made
   * interactive again) once this time has passed.
   */
  settledAt: number;
  place(t: number): CardPlacement;
  draw(ctx: CanvasRenderingContext2D, t: number): void;
}

export type Builder = (
  stage: StageGeometry,
  rng: Rng,
  palette: readonly string[],
) => ConstructionScene;

export const HIDDEN: CardPlacement = { opacity: 0 };

/** Fades the card in where it rests, from a touch smaller. */
export const fadeInAtRest = (
  t: number,
  from: number,
  to: number,
): CardPlacement => {
  if (t < from) return HIDDEN;
  const p = easeOut(seg(t, from, to));
  return { opacity: p, scale: lerp(0.96, 1, p) };
};

/** Props fade in over the first moment and out over the last. */
export const propsAlpha = (t: number) =>
  seg(t, 0, 0.4) * (1 - seg(t, 6.4, 7.2));

// Construction colours. Mid-tones that read on a light and a dark page.
export const BRICKS = ['#B45309', '#C2410C', '#9A3412', '#D97706', '#B91C1C'];
export const SAFETY = ['#F59E0B', '#F97316', '#FBBF24', '#EAB308'];
export const DIRT = ['#92400E', '#78350F', '#A16207'];
export const CONCRETE = ['#9CA3AF', '#6B7280', '#A8A29E'];
export const YELLOW = '#F59E0B';
export const YELLOW_DARK = '#B45309';
export const ORANGE = '#F97316';
export const STEEL = '#475569';
export const STEEL_LIGHT = '#94A3B8';
export const TYRE = '#1F2937';
export const GLASS = '#BAE6FD';
export const CONCRETE_GREY = '#9CA3AF';

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

export function circle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
) {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
}

export interface Dot extends Point {
  r: number;
  color: string;
}

/** Groups still dots by colour once, so a frame fills each colour once. */
export function dotsByColour(dots: readonly Dot[]): Map<string, Dot[]> {
  const groups = new Map<string, Dot[]>();
  for (const dot of dots) {
    const group = groups.get(dot.color);
    if (group) group.push(dot);
    else groups.set(dot.color, [dot]);
  }
  return groups;
}

/**
 * Fills many small circles with one path per colour: thousands of stones at
 * 4K are a handful of fills, not thousands. `skip` leaves a dot out.
 */
export function fillDots(
  ctx: CanvasRenderingContext2D,
  groups: ReadonlyMap<string, readonly Dot[]>,
  skip?: (dot: Dot) => boolean,
) {
  for (const [color, dots] of groups) {
    ctx.fillStyle = color;
    ctx.beginPath();
    for (const dot of dots) {
      if (skip?.(dot)) continue;
      ctx.moveTo(dot.x + dot.r, dot.y);
      ctx.arc(dot.x, dot.y, dot.r, 0, TAU);
    }
    ctx.fill();
  }
}

export function drawBrick(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
  rotation = 0,
) {
  // A wall of still bricks is most of what is on screen, so skip the
  // transform for those.
  const turned = rotation !== 0;
  let left = x;
  let top = y;
  if (turned) {
    ctx.save();
    ctx.translate(x + w / 2, y + h / 2);
    ctx.rotate(rotation);
    left = -w / 2;
    top = -h / 2;
  }
  ctx.fillStyle = color;
  roundRect(ctx, left, top, w, h, 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,.2)';
  ctx.fillRect(left + 2, top + 1, w - 4, 3);
  ctx.fillStyle = 'rgba(0,0,0,.2)';
  ctx.fillRect(left + 2, top + h - 3, w - 4, 2);
  if (turned) ctx.restore();
}

export function drawWheel(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  spin: number,
) {
  ctx.fillStyle = TYRE;
  circle(ctx, x, y, r);
  ctx.fill();
  ctx.strokeStyle = '#4B5563';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.fillStyle = STEEL_LIGHT;
  circle(ctx, x, y, r * 0.48);
  ctx.fill();
  ctx.strokeStyle = STEEL;
  ctx.lineWidth = 2;
  for (let i = 0; i < 3; i += 1) {
    const angle = spin + (i * TAU) / 3;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(angle) * r * 0.45, y + Math.sin(angle) * r * 0.45);
    ctx.stroke();
  }
}

export function drawCone(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  h: number,
  scale: number,
) {
  if (scale <= 0) return;
  ctx.save();
  ctx.translate(x, base);
  ctx.scale(scale, scale);
  const w = h * 0.62;
  ctx.fillStyle = '#EA580C';
  roundRect(ctx, -w * 0.7, -6, w * 1.4, 6, 2);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-w / 2, -6);
  ctx.lineTo(-w * 0.1, -h);
  ctx.lineTo(w * 0.1, -h);
  ctx.lineTo(w / 2, -6);
  ctx.closePath();
  ctx.fillStyle = ORANGE;
  ctx.fill();
  ctx.fillStyle = '#F8FAFC';
  for (const band of [0.35, 0.62]) {
    const halfWidth = lerp(w / 2, w * 0.1, band) - 1;
    ctx.fillRect(-halfWidth, -h * band - h * 0.07, halfWidth * 2, h * 0.12);
  }
  ctx.restore();
}

/** A thrown piece: launched at `te` from (x, y), under gravity `g`. */
export interface Thrown {
  x: number;
  y: number;
  vx: number;
  vy: number;
  g: number;
  te: number;
  life: number;
}

export interface ThrownAt extends Point {
  age: number;
  /** 0..1 through its life. */
  k: number;
}

export function thrownAt(piece: Thrown, t: number): ThrownAt | null {
  const age = t - piece.te;
  if (age < 0 || age > piece.life) return null;
  return {
    age,
    k: age / piece.life,
    x: piece.x + piece.vx * age,
    y: piece.y + piece.vy * age + 0.5 * piece.g * age * age,
  };
}

export interface Chunk extends Thrown {
  size: number;
  spin: number;
  color: string;
}

export function drawChunks(
  ctx: CanvasRenderingContext2D,
  chunks: readonly Chunk[],
  t: number,
) {
  for (const chunk of chunks) {
    const at = thrownAt(chunk, t);
    if (!at) continue;
    ctx.globalAlpha = 1 - seg(at.k, 0.7, 1);
    ctx.fillStyle = chunk.color;
    ctx.save();
    ctx.translate(at.x, at.y);
    ctx.rotate(chunk.spin * at.age);
    ctx.fillRect(
      -chunk.size / 2,
      -chunk.size / 2,
      chunk.size,
      chunk.size * 0.8,
    );
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

export interface Puff extends Thrown {
  size: number;
}

export function makePuffs(
  rng: Rng,
  count: number,
  from: number,
  to: number,
  y: number,
  te: number,
  spread = 1,
): Puff[] {
  return Array.from({ length: count }, () => ({
    x: rand(rng, from, to),
    y: y + rand(rng, -6, 6),
    vx: rand(rng, -70, 70) * spread,
    vy: rand(rng, -50, -5),
    g: 0,
    te: te + rand(rng, 0, 0.12),
    life: rand(rng, 0.6, 1.2),
    size: rand(rng, 10, 24),
  }));
}

export function drawPuffs(
  ctx: CanvasRenderingContext2D,
  puffs: readonly Puff[],
  t: number,
) {
  ctx.fillStyle = STEEL_LIGHT;
  for (const puff of puffs) {
    const at = thrownAt(puff, t);
    if (!at) continue;
    ctx.globalAlpha = (1 - at.k) * 0.45;
    circle(ctx, at.x, at.y, puff.size * (0.4 + at.k));
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

/** Walks the card's edge, `pad` outside it, clockwise from the top-left. */
export function cardPerimeter(stage: StageGeometry, pad: number) {
  const left = stage.left - pad;
  const top = stage.top - pad;
  const w = stage.cardWidth + pad * 2;
  const h = stage.cardHeight + pad * 2;
  const total = 2 * (w + h);
  const at = (distance: number) => {
    let d = ((distance % total) + total) % total;
    if (d < w) return { x: left + d, y: top, nx: 0, ny: -1 };
    d -= w;
    if (d < h) return { x: left + w, y: top + d, nx: 1, ny: 0 };
    d -= h;
    if (d < w) return { x: left + w - d, y: top + h, nx: 0, ny: 1 };
    d -= w;
    return { x: left, y: top + h - d, nx: -1, ny: 0 };
  };
  return { total, at };
}
